/**
 * Japanese Language Profile
 *
 * SOV word order, particles (を, に, で, etc.), no spaces between words.
 * Agglutinative language with rich verb conjugation.
 */

import type { LanguageProfile } from './types';

export const japaneseProfile: LanguageProfile = {
  code: 'ja',
  name: 'Japanese',
  nativeName: '日本語',
  regions: ['east-asian', 'priority'],
  direction: 'ltr',
  script: 'cjk',
  wordOrder: 'SOV',
  markingStrategy: 'particle',
  usesSpaces: false,
  // Japanese uses verb stem/masu-stem form, no clear infinitive/imperative distinction
  // for UI commands. Uses katakana loanwords (トグル) or native stems (切り替え)
  defaultVerbForm: 'base',
  verb: {
    position: 'end',
    suffixes: ['る', 'て', 'た', 'ます', 'ない'],
    subjectDrop: true,
  },
  references: {
    me: '自分', // "self" - in hyperscript context, refers to current element
    it: 'それ', // "it"
    you: 'あなた', // "you"
    result: '結果',
    event: 'イベント',
    target: 'ターゲット',
    body: 'ボディ',
    document: 'ドキュメント',
    window: 'ウィンドウ',
    detail: '詳細',
  },
  // A query's scope before it (`#list の中の <li/>`; M2 N3).
  queryScopeFirst: { marker: 'の中の', queryIn: 'の中' },
  possessive: {
    marker: 'の',
    markerPosition: 'between',
    // In Japanese: 自分の value (jibun no value) = "my value"
    keywords: {
      私の: 'me', // watashi no (my)
      あなたの: 'you', // anata no (your)
      その: 'it', // sono (its)
    },
  },
  roleMarkers: {
    patient: { primary: 'を', position: 'after' },
    destination: { primary: 'に', alternatives: ['へ', 'で', 'の'], position: 'after' },
    source: { primary: 'から', position: 'after' },
    style: { primary: 'で', position: 'after' },
    event: { primary: 'を', position: 'after' }, // Event as object marker
    // Possession marker for "X's Y" patterns
    // Note: の is used between target and patient: #button の .active
  },
  keywords: {
    // Class/Attribute operations
    toggle: {
      primary: '切り替え',
      alternatives: ['切り替える', 'トグル', 'トグルする'],
      normalized: 'toggle',
    },
    add: { primary: '追加', alternatives: ['追加する', '加える'], normalized: 'add' },
    remove: { primary: '削除', alternatives: ['削除する', '取り除く'], normalized: 'remove' },
    // Content operations
    put: { primary: '置く', alternatives: ['入れる'], normalized: 'put' },
    append: { primary: '末尾追加', alternatives: ['末尾に追加', 'アペンド'], normalized: 'append' },
    prepend: {
      primary: '先頭追加',
      alternatives: ['先頭に追加', 'プリペンド'],
      normalized: 'prepend',
    },
    take: { primary: '取る', normalized: 'take' }, // Removed 取得 (belongs to get)
    make: { primary: '作る', alternatives: ['作成'], normalized: 'make' },
    clone: { primary: '複製', alternatives: ['クローン'], normalized: 'clone' },
    swap: { primary: '交換', alternatives: ['スワップ', '入れ替え'], normalized: 'swap' },
    morph: { primary: '変形', alternatives: ['モーフ', '変換'], normalized: 'morph' },
    // Variable operations
    set: { primary: '設定', alternatives: ['設定する'], normalized: 'set' },
    get: { primary: '取得', alternatives: ['取得する', 'ゲット'], normalized: 'get' },
    increment: {
      primary: '増加',
      alternatives: ['増やす', 'インクリメント'],
      normalized: 'increment',
    },
    decrement: {
      primary: '減少',
      alternatives: ['減らす', 'デクリメント'],
      normalized: 'decrement',
    },
    log: { primary: '記録', alternatives: ['ログ', '出力'], normalized: 'log' },
    // Visibility
    show: { primary: '表示', alternatives: ['表示する', '見せる'], normalized: 'show' },
    hide: { primary: '隠す', alternatives: ['非表示', '非表示にする'], normalized: 'hide' },
    transition: {
      primary: '遷移',
      alternatives: ['トランジション', 'アニメーション'],
      normalized: 'transition',
    },
    // Events
    on: { primary: 'で', alternatives: ['時'], normalized: 'on' },
    trigger: { primary: '引き金', alternatives: ['発火', 'トリガー'], normalized: 'trigger' },
    send: { primary: '送る', alternatives: ['送信'], normalized: 'send' },
    // DOM focus
    // 集中 dropped: it means mental concentration and is never used for UI focus,
    // so it would mislead a beginner rather than help one.
    focus: { primary: 'フォーカス', normalized: 'focus' },
    // 'ぼかし' is the image-processing sense of blur (Photoshop ぼかしフィルター /
    // ガウスぼかし) and carries no focus meaning in Japanese at all — a developer
    // reading it would expect a visual effect.
    //
    // フォーカスアウト demoted from primary 2026-07-28 (independent verification
    // pass): it is the established Japanese name for the DOM `focusout` event,
    // which is a DIFFERENT event from `blur` — same moment, but focusout bubbles
    // and blur does not. Naming blur with it teaches a false equivalence and
    // spends the only term available if focusout is ever published. フォーカス解除
    // is the native, unambiguous name for losing focus and names no other API.
    // Both demoted forms stay as parse alternatives, so existing pages keep working.
    blur: {
      primary: 'フォーカス解除',
      alternatives: ['フォーカスアウト', 'ブラー', 'ぼかし'],
      normalized: 'blur',
    },
    // Phase 1 (v0.9.90): DOM / form state / debug
    // Batch 3: do NOT add bare 空 here — probed: registering it as an empty
    // keyword injects a phantom `empty` command into the corpus-hot `is empty`
    // expression rows (である 空), an R0-precision regression. The empty-COMMAND
    // render gap (dict renders 空, parses null) is waived instead.
    empty: { primary: '空に', alternatives: ['空にする'], normalized: 'empty' },
    open: { primary: '開く', alternatives: ['オープン'], normalized: 'open' },
    close: { primary: '閉じる', alternatives: ['クローズ'], normalized: 'close' },
    select: { primary: '選ぶ', alternatives: ['セレクト'], normalized: 'select' },
    clear: { primary: 'クリア', alternatives: ['消去'], normalized: 'clear' },
    reset: { primary: 'リセット', alternatives: ['再設定'], normalized: 'reset' },
    breakpoint: { primary: 'ブレークポイント', normalized: 'breakpoint' },
    // Navigation
    go: { primary: '移動', alternatives: ['行く', 'ナビゲート'], normalized: 'go' },
    scroll: { primary: 'スクロール', normalized: 'scroll' },
    push: { primary: 'プッシュ', normalized: 'push' },
    replace: { primary: '置換', alternatives: ['置き換え'], normalized: 'replace' },
    process: { primary: '処理', normalized: 'process' },
    // Async
    wait: { primary: '待つ', alternatives: ['待機'], normalized: 'wait' },
    fetch: { primary: 'フェッチ', normalized: 'fetch' },
    settle: { primary: '安定', alternatives: ['落ち着く'], normalized: 'settle' },
    // Control flow
    if: { primary: 'もし', normalized: 'if' },
    when: { primary: 'とき', alternatives: ['ときに'], normalized: 'when' },
    // Reactive `when <expr> changes` trigger word — synced VERBATIM from the i18n
    // dictionary (`changes`), which is what wrote every stored corpus row; the V1
    // vocab gate requires the two surfaces to agree. Native review pending — see
    // NATIVE_REVIEW_NEEDED.md § "Reactive `when … changes`".
    changes: { primary: '変わったら', normalized: 'changes' },
    where: { primary: 'どこ', normalized: 'where' },
    else: { primary: 'そうでなければ', alternatives: ['それ以外'], normalized: 'else' },
    // `ない限り` ("as long as not" = unless). Deliberately NOT `でなければ`: that
    // begins with the `で` particle, which the tokenizer peels off the front
    // (`で`+`なければ`), shattering the marker — registering it as a keyword does
    // NOT win against the particle extractor. `ない限り` starts with `な` (not a
    // particle), so it tokenizes as a single `unless` token (the same reason else
    // `そうでなければ` tokenizes clean), and the trailing-`unless` guard recovers the
    // clause. See docs-internal/HANDOFF-unless-condition-tokenizer.md.
    unless: { primary: 'ない限り', normalized: 'unless' },
    repeat: { primary: '繰り返し', alternatives: ['繰り返す', 'リピート'], normalized: 'repeat' },
    for: { primary: 'ために', alternatives: ['各'], normalized: 'for' },
    while: { primary: 'の間', alternatives: ['間'], normalized: 'while' },
    // `repeat forever` loop keyword, so the loopType types as `:literal` like EN (see
    // spanish.ts). The language's own word since M2 (V1); the reader takes English's.
    forever: { primary: 'ずっと', normalized: 'forever', alternatives: ['forever'] },
    continue: { primary: '続ける', alternatives: ['継続'], normalized: 'continue' },
    halt: { primary: '停止', alternatives: ['止める', 'ハルト'], normalized: 'halt' },
    throw: { primary: '投げる', alternatives: ['スロー'], normalized: 'throw' },
    call: { primary: '呼び出し', alternatives: ['コール', '呼ぶ'], normalized: 'call' },
    return: { primary: '戻る', alternatives: ['返す', 'リターン'], normalized: 'return' },
    then: { primary: 'それから', alternatives: ['次に', 'ならば', 'なら'], normalized: 'then' },
    and: { primary: 'また', alternatives: ['と', 'そして'], normalized: 'and' },
    // Comparison operator (`target matches .x`). Deferred by the Phase 2 `matches`
    // slice because ja's operand ALSO leaked (`references.target` carried ターゲット
    // while the dict emits 対象), and registering the operator without its operand is
    // worse than neither: modal-close-backdrop ja passed R2 only BY ACCIDENT — the
    // unparsed condition was dropped, so `hide` ran unconditionally and coincidentally
    // matched the en DOM effect. `matches` alone would parse the condition into a real
    // comparison whose operand 対象 evaluates to undefined, stopping `hide` and
    // flipping R2 pass→fail at tolerance 0. Landing WITH the 対象 EXTRAS entry
    // (japanese.ts tokenizer) renders `target matches .modal-backdrop`, byte-identical
    // to en. Not an ActionType and has no command schema, so no pattern is generated.
    matches: { primary: '一致する', normalized: 'matches' },
    // Existence operator (`if #modal exists`). Same seam as `matches`: without the
    // keyword the surface stays an identifier and leaks verbatim into the
    // condition's raw expression (if-exists). Neither an ActionType nor a command
    // schema, so no pattern is generated from it.
    exists: { primary: '存在する', normalized: 'exists' },
    // Copula (`if result is false`, `if my value is empty`). Without the keyword the
    // surface stays an identifier and leaks verbatim into the condition's raw
    // expression, which the core expression parser reads as English. Neither an
    // ActionType nor a command schema, so no pattern is generated from it.
    is: { primary: 'である', normalized: 'is' },
    // Negative-existence operator (`if no dragHandle set dragHandle to me`). Same
    // seam as `exists`: without the keyword the surface stays an identifier and
    // leaks verbatim into the condition's raw expression (behavior-draggable).
    // Neither an ActionType nor a command schema, so no pattern is generated from it.
    no: { primary: 'ない', normalized: 'no' },
    // 終了 removed: it is the i18n dict's `exit` emission (ja.ts), so listing it
    // as an `end` alternative made an `exit` inside `if … exit … end` read as the
    // block terminator and collapse the handler body (behavior-sortable). 終わり is
    // the dict's `end` emission; おわり the kana variant.
    end: { primary: '終わり', alternatives: ['おわり'], normalized: 'end' },
    // Advanced
    js: { primary: 'JS実行', alternatives: ['js'], normalized: 'js' },
    async: { primary: '非同期', alternatives: ['アシンク'], normalized: 'async' },
    tell: { primary: '伝える', alternatives: ['テル'], normalized: 'tell' },
    default: { primary: '既定', alternatives: ['デフォルト'], normalized: 'default' },
    // イニット dropped: essentially no currency — Japanese developers write init
    // in Latin letters or say イニシャライザ for the initializer.
    init: { primary: '初期化', normalized: 'init' },
    behavior: { primary: '振る舞い', alternatives: ['ビヘイビア'], normalized: 'behavior' },
    install: { primary: 'インストール', alternatives: ['導入'], normalized: 'install' },
    measure: { primary: '測定', alternatives: ['計測', 'メジャー'], normalized: 'measure' },
    beep: { primary: 'ビープ', normalized: 'beep' },
    ask: { primary: '尋ねる', normalized: 'ask' },
    answer: { primary: '答える', normalized: 'answer' },
    break: { primary: '中断', normalized: 'break' },
    copy: { primary: 'コピー', normalized: 'copy' },
    exit: { primary: '退出', normalized: 'exit' },
    pick: { primary: '選択', normalized: 'pick' },
    render: { primary: '描画', normalized: 'render' },
    // Modifiers
    into: { primary: 'へ', alternatives: ['に'], normalized: 'into' },
    before: { primary: '前に', alternatives: ['前'], normalized: 'before' },
    after: { primary: '後に', alternatives: ['後'], normalized: 'after' },
    // Event modifiers (for repeat until event)
    until: { primary: 'まで', alternatives: ['迄'], normalized: 'until' },
    event: { primary: 'イベント', alternatives: ['事象'], normalized: 'event' },
    from: { primary: 'から', normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-接続`, `hx-ライブ`, etc. Noun
    // forms (rather than verb stems) since these appear as attribute
    // suffixes, not verbs in a hyperscript sentence.
    connect: { primary: '接続', alternatives: ['接続する'], normalized: 'connect' },
    stream: { primary: 'ストリーム', alternatives: ['配信'], normalized: 'stream' },
    live: { primary: 'ライブ', alternatives: ['実時間'], normalized: 'live' },
    socket: { primary: 'ソケット', alternatives: ['websocket'], normalized: 'socket' },
    // Reactive / realtime commands
    bind: { primary: 'バインド', alternatives: ['結びつける', 'bind'], normalized: 'bind' },
    intercept: {
      primary: 'インターセプト',
      alternatives: ['傍受', 'intercept'],
      normalized: 'intercept',
    },
    worker: { primary: 'ワーカー', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['イベントソース'],
      normalized: 'eventsource',
    },
  },
  tokenization: {
    particles: ['を', 'に', 'で', 'から', 'の', 'が', 'は', 'も', 'へ', 'と'],
    boundaryStrategy: 'particle',
  },
  // Japanese writes a particle onto its noun (`#outに`, `自分から`; M2 N5).
  attachedMarkers: {
    markers: ['を', 'に', 'へ', 'で', 'の', 'から', 'として', 'の間で'],
    afterWords: 'all',
  },
  eventHandler: {
    // Event marker: で (at/on/when), used in SOV pattern
    // Pattern: [event] で [destination] の [patient] を [action]
    // Example: クリック で #button の .active を 切り替え
    // Note: に is NOT an event marker - it marks direction/destination
    // で specifically marks the means/method of action (by/via)
    eventMarker: { primary: 'で', position: 'after' },
    temporalMarkers: ['時', 'とき'], // temporal markers that can optionally appear
    // The head is `クリックで` (N4): the fused `{event} で …` patterns would
    // otherwise read every handler, with their hand-built body.
    headOutranksFused: true,
  },
};
